/*********************
* Intervention base class *
*
* This class is extended or instantiated.
*
* An instance of this class represents an intervention type/mode's applicability,
* given the context of a particular pass. In other words, an "intervention" object
* tracks the conditions under which it's type/mode are applicable.
*
* The "runRules" method returns a bool representing whether the type/mode is a valid selection,
* given the state passed in during the course of a particular run through the associated pass.
*********************/
import getRule from './rules/ruleFactory';
import { LOG_DECISION_CONTEXT } from '../config';
import { ruleTypes, phraseLevelRuleInputTypes} from '../constants';

export default class Intervention {
  constructor(
      type, // Required - intervention type
      requiredFields=[], // Whether the word that we're intervening on needs to have a certain set of required fields
      ruleData={}, // A set of rules to initialize the intervention with
      mode='', // What mode the intervention is in (e.g. interactive versus autoplay elkonin)
    ){
    this.log = {};
    this.type = type; // Intervention type

    // Prevent null/false input params
    this.requiredFields = requiredFields ? requiredFields : [];
    this.mode = mode ? mode : '';

    // Initialize this intervention's rules, instantiating them based on Config-specifications
    // (via key/value ruleData tuples)
    this.rules = [];
    Object.entries(ruleData).forEach(([ruleType, logic]) => {
      // combo rules can occur multiple times, and therefore
      // need to be striped of an identifying charecter
      // (e.g. or1, or2)
      if(ruleType.includes(ruleTypes.OR) && ruleType.length < 4) {
        ruleType = ruleTypes.OR;
      } else if(ruleType.includes(ruleTypes.AND)  && ruleType.length < 5) {
        ruleType = ruleTypes.AND;
      }
      this.addRule(getRule(ruleType, logic, type));
    });

    // Initialize a string label for this rule, for use while
    // logging decision context (if logging is enabled)
    let modeString = this.mode;
    if(modeString != '') {
      modeString = "_" + modeString;
    }
    this.label = this.type + modeString;
  }

  ///// Add Rule
  addRule(rule) {
    // Add the rule's inputTypes to the Intervention's requiredFields
    rule.getInputTypes().forEach(inputType => {
      if(inputType && !this.requiredFields.includes(inputType)) {
        this.requiredFields.push(inputType);
      };
    });

    // Add the rule to "rules"
    this.rules.push(rule);
  }

  getType() {
    return this.type;
  }

  ////// Run Rules
  // Returns a bool, representing whether the specified state fulfills
  // this intervention's rule set.
  //
  // Also returns an object, toggled via logging config, which
  // describes what rule conditions were met.
  runRules(state) {
    this.log.rules = [];

    // First, check for required fields
    if(this.requiredFields) {
      let requiredFieldsExist = true;
      let missingFields = [];
      let decisionContext;
      this.requiredFields.forEach(field => {
        if(state.endOfPhrase || (!state.endOfPhrase && !phraseLevelRuleInputTypes.includes(field))) {
          if((!state[field] && state[field] !== 0) || state[field] === '' || (Array.isArray(state[field]) && state[field].length === 0)) {
            missingFields.push(field);
            requiredFieldsExist = false;
            if(LOG_DECISION_CONTEXT) decisionContext = [('DISQUALIFIED_MISSING_FIELD - ' + field)];
          }
        }
      });
      if(!requiredFieldsExist) {
        LOG_DECISION_CONTEXT &&  this.log.rules.push(`MISSING_FIELD: '${state.word}' missing ${missingFields.join(', ')}`);
        return [
          false,
          {[this.label + 'FAILED--DISQUALIFIED_MISSING_FIELD']: decisionContext},
          this.log
        ];
      }
    }

    // Next, run the rule set
    let allRuleConditionsMet = true;
    let ruleDecisionContext = []; // LOGGING VAR
    let failedOnLogging = '';
    this.rules.forEach(rule => {
      let {stateMeetsRuleCondition, decisionContext, log} = rule.run(state);
      if(LOG_DECISION_CONTEXT) {
        ruleDecisionContext.push(decisionContext);
        this.log.rules.push(log);
      }

      if(!stateMeetsRuleCondition) {
        failedOnLogging = 'FAILED--' + rule.getRuleType();
      }
      allRuleConditionsMet = allRuleConditionsMet && stateMeetsRuleCondition;
    });

    let passConditionLogging = allRuleConditionsMet ? 'PASSED' : failedOnLogging;
    return [allRuleConditionsMet, {[this.label + '_' + passConditionLogging]: ruleDecisionContext}, this.log];
  }

}
